// src/mocks/dam.ts
// Мок-каталог корпоративного DAM-хранилища для страницы «Источники данных».
// Реальный API файлов (/projects/context-documents) требует chat_id и общий
// каталог не отдаёт, поэтому хранилище полностью имитируется (см. спеку FR-1.3).

export interface DamFolder {
  id: string;
  name: string;
}

export type DamDocType =
  | 'Регламент'
  | 'Договор'
  | 'Инструкция'
  | 'Презентация'
  | 'Отчёт'
  | 'Политика'
  | 'Шаблон'
  | 'Загруженный'
  | 'Сгенерированный';

export interface DamFile {
  id: string;
  name: string;
  ext: 'pdf' | 'docx' | 'xlsx' | 'pptx' | 'txt' | 'md';
  folderId: string;
  sizeKb: number;
  author: string;
  department: string;
  docType: DamDocType;
  updatedAt: string; // YYYY-MM-DD
}

export const DAM_FOLDERS: DamFolder[] = [
  { id: 'cb-regulations', name: 'Регламенты ЦБ' },
  { id: 'contracts', name: 'Договоры' },
  { id: 'legal', name: 'Юридический отдел' },
  { id: 'hr', name: 'HR-документы' },
  { id: 'credit', name: 'Кредитование' },
  { id: 'compliance', name: 'Комплаенс и ПОД/ФТ' },
  { id: 'presentations', name: 'Презентации' },
  { id: 'reports', name: 'Отчётность' },
  { id: 'templates', name: 'Шаблоны документов' },
];

const f = (
  id: string,
  name: string,
  ext: DamFile['ext'],
  folderId: string,
  sizeKb: number,
  author: string,
  department: string,
  docType: DamDocType,
  updatedAt: string
): DamFile => ({ id, name, ext, folderId, sizeKb, author, department, docType, updatedAt });

const LEGAL = 'Юридический блок';
const HR = 'Управление персоналом';
const OPS = 'Операционный департамент';
const RISK = 'Департамент рисков';

export const DAM_FILES: DamFile[] = [
  // ── Регламенты ЦБ ──
  f(
    'cb-1',
    'Положение ЦБ № 716-П (операционные риски)',
    'pdf',
    'cb-regulations',
    2840,
    'Волков Андрей',
    RISK,
    'Регламент',
    '2026-05-28'
  ),
  f(
    'cb-2',
    'Положение ЦБ № 683-П (защита информации)',
    'pdf',
    'cb-regulations',
    1960,
    'Волков Андрей',
    RISK,
    'Регламент',
    '2026-05-19'
  ),
  f(
    'cb-3',
    'Инструкция ЦБ № 199-И (нормативы банков)',
    'pdf',
    'cb-regulations',
    4320,
    'Иванова Анна',
    LEGAL,
    'Инструкция',
    '2026-04-30'
  ),
  f(
    'cb-4',
    'Указание ЦБ № 5782-У (отчётность)',
    'pdf',
    'cb-regulations',
    880,
    'Волков Андрей',
    RISK,
    'Регламент',
    '2026-04-22'
  ),
  f(
    'cb-5',
    'Методичка по 115-ФЗ для операционистов',
    'docx',
    'cb-regulations',
    460,
    'Смирнова Ольга',
    OPS,
    'Инструкция',
    '2026-05-30'
  ),
  f(
    'cb-6',
    'Обзор изменений регулирования Q2 2026',
    'pdf',
    'cb-regulations',
    1240,
    'Иванова Анна',
    LEGAL,
    'Отчёт',
    '2026-06-01'
  ),
  // ── Договоры ──
  f(
    'ct-1',
    'Договор РКО (типовая форма)',
    'docx',
    'contracts',
    310,
    'Иванова Анна',
    LEGAL,
    'Шаблон',
    '2026-05-12'
  ),
  f(
    'ct-2',
    'Кредитный договор ЮЛ (типовая форма)',
    'docx',
    'contracts',
    420,
    'Иванова Анна',
    LEGAL,
    'Шаблон',
    '2026-05-25'
  ),
  f(
    'ct-3',
    'Договор поставки ПО «Диасофт» 2026',
    'pdf',
    'contracts',
    1820,
    'Петров Михаил',
    LEGAL,
    'Договор',
    '2026-05-21'
  ),
  f(
    'ct-4',
    'Договор аренды офиса Лесная 7',
    'pdf',
    'contracts',
    2210,
    'Петров Михаил',
    LEGAL,
    'Договор',
    '2026-04-18'
  ),
  f(
    'ct-5',
    'Договор эквайринга (ред. 3)',
    'docx',
    'contracts',
    380,
    'Иванова Анна',
    LEGAL,
    'Договор',
    '2026-05-07'
  ),
  f(
    'ct-6',
    'ДКП векселей № 14-2026',
    'pdf',
    'contracts',
    540,
    'Петров Михаил',
    LEGAL,
    'Договор',
    '2026-04-25'
  ),
  f(
    'ct-7',
    'Агентский договор страхования',
    'docx',
    'contracts',
    350,
    'Иванова Анна',
    LEGAL,
    'Договор',
    '2026-05-29'
  ),
  f(
    'ct-8',
    'NDA с подрядчиком «Синимекс»',
    'pdf',
    'contracts',
    290,
    'Петров Михаил',
    LEGAL,
    'Договор',
    '2026-06-02'
  ),
  // ── Юридический отдел ──
  f(
    'lg-1',
    'Правовое заключение по цифровому рублю',
    'docx',
    'legal',
    680,
    'Иванова Анна',
    LEGAL,
    'Отчёт',
    '2026-05-31'
  ),
  f(
    'lg-2',
    'Претензия Альфа-Лизинг (исх. 245)',
    'pdf',
    'legal',
    410,
    'Петров Михаил',
    LEGAL,
    'Договор',
    '2026-05-26'
  ),
  f(
    'lg-3',
    'Судебная практика по 353-ФЗ (обзор)',
    'docx',
    'legal',
    920,
    'Иванова Анна',
    LEGAL,
    'Отчёт',
    '2026-05-15'
  ),
  f(
    'lg-4',
    'Доверенность ген. директора (шаблон)',
    'docx',
    'legal',
    120,
    'Петров Михаил',
    LEGAL,
    'Шаблон',
    '2026-04-14'
  ),
  f(
    'lg-5',
    'Регламент договорной работы v4',
    'pdf',
    'legal',
    1340,
    'Иванова Анна',
    LEGAL,
    'Регламент',
    '2026-05-08'
  ),
  f(
    'lg-6',
    'Протокол правления № 18',
    'pdf',
    'legal',
    260,
    'Петров Михаил',
    LEGAL,
    'Отчёт',
    '2026-05-27'
  ),
  // ── HR-документы ──
  f(
    'hr-1',
    'Политика отпусков 2026',
    'pdf',
    'hr',
    540,
    'Смирнова Ольга',
    HR,
    'Политика',
    '2026-04-28'
  ),
  f(
    'hr-2',
    'Положение об оплате труда',
    'pdf',
    'hr',
    760,
    'Смирнова Ольга',
    HR,
    'Политика',
    '2026-04-12'
  ),
  f(
    'hr-3',
    'Оффер: старший юрист (шаблон)',
    'docx',
    'hr',
    95,
    'Смирнова Ольга',
    HR,
    'Шаблон',
    '2026-05-23'
  ),
  f('hr-4', 'План найма Q3 2026', 'xlsx', 'hr', 210, 'Смирнова Ольга', HR, 'Отчёт', '2026-06-01'),
  f(
    'hr-5',
    'Регламент адаптации новых сотрудников',
    'docx',
    'hr',
    430,
    'Смирнова Ольга',
    HR,
    'Регламент',
    '2026-05-05'
  ),
  f(
    'hr-6',
    'Опрос вовлечённости: итоги мая',
    'pptx',
    'hr',
    3120,
    'Смирнова Ольга',
    HR,
    'Презентация',
    '2026-05-30'
  ),
  f(
    'hr-7',
    'Должностная инструкция операциониста',
    'docx',
    'hr',
    180,
    'Смирнова Ольга',
    HR,
    'Инструкция',
    '2026-04-20'
  ),
  // ── Кредитование ──
  f(
    'cr-1',
    'Кредитная политика 2026',
    'pdf',
    'credit',
    1980,
    'Волков Андрей',
    RISK,
    'Политика',
    '2026-04-15'
  ),
  f(
    'cr-2',
    'Регламент андеррайтинга ЮЛ',
    'pdf',
    'credit',
    1450,
    'Волков Андрей',
    RISK,
    'Регламент',
    '2026-05-11'
  ),
  f(
    'cr-3',
    'Скоринговая модель МСБ v2.4 (описание)',
    'docx',
    'credit',
    820,
    'Волков Андрей',
    RISK,
    'Инструкция',
    '2026-05-24'
  ),
  f(
    'cr-4',
    'Портфель кредитов МСБ: май 2026',
    'xlsx',
    'credit',
    1640,
    'Волков Андрей',
    OPS,
    'Отчёт',
    '2026-06-02'
  ),
  f(
    'cr-5',
    'Анкета заёмщика ЮЛ (шаблон)',
    'docx',
    'credit',
    140,
    'Смирнова Ольга',
    OPS,
    'Шаблон',
    '2026-04-19'
  ),
  f(
    'cr-6',
    'Методика оценки залогов',
    'pdf',
    'credit',
    1120,
    'Волков Андрей',
    RISK,
    'Инструкция',
    '2026-05-16'
  ),
  f(
    'cr-7',
    'Просроченная задолженность: отчёт Q1',
    'xlsx',
    'credit',
    980,
    'Волков Андрей',
    RISK,
    'Отчёт',
    '2026-04-10'
  ),
  // ── Комплаенс и ПОД/ФТ ──
  f(
    'cp-1',
    'Правила внутреннего контроля ПОД/ФТ',
    'pdf',
    'compliance',
    2350,
    'Иванова Анна',
    LEGAL,
    'Политика',
    '2026-05-14'
  ),
  f(
    'cp-2',
    'Перечень критериев сомнительных операций',
    'docx',
    'compliance',
    510,
    'Волков Андрей',
    RISK,
    'Инструкция',
    '2026-05-22'
  ),
  f(
    'cp-3',
    'Регламент KYC для новых клиентов',
    'pdf',
    'compliance',
    890,
    'Иванова Анна',
    LEGAL,
    'Регламент',
    '2026-04-26'
  ),
  f(
    'cp-4',
    'Отчёт о подозрительных операциях: май',
    'xlsx',
    'compliance',
    430,
    'Волков Андрей',
    RISK,
    'Отчёт',
    '2026-06-01'
  ),
  f(
    'cp-5',
    'Памятка по санкционному комплаенсу',
    'pdf',
    'compliance',
    340,
    'Иванова Анна',
    LEGAL,
    'Инструкция',
    '2026-05-18'
  ),
  f(
    'cp-6',
    'Политика обработки персональных данных',
    'pdf',
    'compliance',
    620,
    'Иванова Анна',
    LEGAL,
    'Политика',
    '2026-04-24'
  ),
  // ── Презентации ──
  f(
    'pr-1',
    'Стратегия банка 2026–2028',
    'pptx',
    'presentations',
    8420,
    'Волков Андрей',
    OPS,
    'Презентация',
    '2026-04-16'
  ),
  f(
    'pr-2',
    'Итоги Q1 2026 для правления',
    'pptx',
    'presentations',
    5260,
    'Волков Андрей',
    OPS,
    'Презентация',
    '2026-04-29'
  ),
  f(
    'pr-3',
    'Продуктовая линейка МСБ',
    'pptx',
    'presentations',
    4180,
    'Смирнова Ольга',
    OPS,
    'Презентация',
    '2026-05-13'
  ),
  f(
    'pr-4',
    'Внедрение ИИ-ассистента: пилот',
    'pptx',
    'presentations',
    3640,
    'Петров Михаил',
    OPS,
    'Презентация',
    '2026-05-27'
  ),
  f(
    'pr-5',
    'Цифровая трансформация: роадмап',
    'pptx',
    'presentations',
    6090,
    'Волков Андрей',
    OPS,
    'Презентация',
    '2026-05-20'
  ),
  // ── Отчётность ──
  f(
    'rp-1',
    'Форма 0409101 (апрель 2026)',
    'xlsx',
    'reports',
    720,
    'Волков Андрей',
    OPS,
    'Отчёт',
    '2026-05-06'
  ),
  f(
    'rp-2',
    'Форма 0409135 (апрель 2026)',
    'xlsx',
    'reports',
    540,
    'Волков Андрей',
    OPS,
    'Отчёт',
    '2026-05-06'
  ),
  f(
    'rp-3',
    'Управленческая отчётность: май 2026',
    'xlsx',
    'reports',
    1890,
    'Волков Андрей',
    OPS,
    'Отчёт',
    '2026-06-02'
  ),
  f(
    'rp-4',
    'KPI операционного блока: апрель',
    'xlsx',
    'reports',
    660,
    'Волков Андрей',
    OPS,
    'Отчёт',
    '2026-05-04'
  ),
  f(
    'rp-5',
    'SLA поддержки: Q1 2026',
    'pdf',
    'reports',
    480,
    'Волков Андрей',
    OPS,
    'Отчёт',
    '2026-04-11'
  ),
  f(
    'rp-6',
    'Аудиторское заключение 2025',
    'pdf',
    'reports',
    3210,
    'Иванова Анна',
    LEGAL,
    'Отчёт',
    '2026-04-08'
  ),
  // ── Шаблоны документов ──
  f(
    'tp-1',
    'Бланк исходящего письма',
    'docx',
    'templates',
    85,
    'Смирнова Ольга',
    OPS,
    'Шаблон',
    '2026-04-09'
  ),
  f(
    'tp-2',
    'Шаблон протокола совещания',
    'docx',
    'templates',
    90,
    'Смирнова Ольга',
    OPS,
    'Шаблон',
    '2026-04-09'
  ),
  f(
    'tp-3',
    'Шаблон служебной записки',
    'docx',
    'templates',
    75,
    'Смирнова Ольга',
    OPS,
    'Шаблон',
    '2026-04-09'
  ),
  f(
    'tp-4',
    'Заявление на отпуск',
    'docx',
    'templates',
    60,
    'Смирнова Ольга',
    HR,
    'Шаблон',
    '2026-04-21'
  ),
  f(
    'tp-5',
    'Шаблон приказа по основной деятельности',
    'docx',
    'templates',
    110,
    'Петров Михаил',
    LEGAL,
    'Шаблон',
    '2026-05-02'
  ),
];

export const DAM_DOC_TYPES: DamDocType[] = [
  'Регламент',
  'Договор',
  'Инструкция',
  'Презентация',
  'Отчёт',
  'Политика',
  'Шаблон',
];

export const DAM_DEPARTMENTS = [LEGAL, HR, OPS, RISK];

export const DAM_EXTENSIONS: DamFile['ext'][] = ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'md'];
